import { usePropertyTypes } from "@/hooks/usePropertyTypes";
import { Listing } from "@/lib/types";
import { formatPrice } from "@/lib/utils";
import { Building, MapPin, ArrowRight } from "lucide-react";
import { memo, useMemo } from "react";
import { Link } from "react-router-dom";
import PropertyFooter from "./property-list-item/PropertyFooter";
import PropertyHeader from "./property-list-item/PropertyHeader";
import PropertyImage from "./property-list-item/PropertyImage";
import PropertyInfo from "./property-list-item/PropertyInfo";
import { useFavorite } from "./property-list-item/useFavorite";
import { Button } from "@/components/ui/button";

interface PropertyListItemProps {
  property: Listing;
  isCompact?: boolean;
}

const PropertyListItem = memo(({ property, isCompact = false }: PropertyListItemProps) => {
  const { isFavorite, handleToggleFavorite } = useFavorite(property);
  const { data: propertyTypes = [] } = usePropertyTypes();

  const isPropertyFeatured = property.is_featured === 1;
  const isPropertyBoosted = property.is_boosted === 1;
  const propertyTypeName = useMemo(() => {
    const type = propertyTypes.find((t) => t.id === property.property_type_id);
    return type?.name || `Type ${property.property_type_id}`;
  }, [propertyTypes, property.property_type_id]);

  return (
    <div className={`bg-white rounded-lg overflow-hidden border border-gray-100 transition-all duration-300 hover:shadow-md ${
      isPropertyFeatured ? "ring-2 ring-propertylk-blue/20" : ""
    }`}>
      <div className="flex flex-col sm:flex-row h-full">
        {/* Image Section - 40% width on desktop */}
        <div className="sm:w-[40%] relative group">
          <PropertyImage
            property={property}
            isFeatured={isPropertyFeatured}
            isBoosted={isPropertyBoosted}
            isCompact={isCompact}
            isFavorite={isFavorite}
            onToggleFavorite={handleToggleFavorite}
          />
        </div>

        {/* Content Section - 60% width on desktop */}
        <div className="sm:w-[60%] p-4 flex flex-col justify-between">
          {/* Property Type Badge */}
          <div className="flex items-center gap-2 mb-2">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
              <Building className="h-3.5 w-3.5" />
              {propertyTypeName}
            </div>
            {property.location?.city && (
              <div className="inline-flex items-center gap-1.5 text-xs text-gray-500">
                <MapPin className="h-3.5 w-3.5" />
                {property.location.city}
              </div>
            )}
          </div>

          {/* Title and Price */}
          <h3 className="text-lg font-semibold mb-2 line-clamp-2">{property.title}</h3>
          <div className="text-xl font-bold text-propertylk-blue mb-3">
            {formatPrice(property.price)}
            {property.listing_type !== 'sale' && <span className="text-sm text-gray-500">/month</span>}
          </div>

          {/* Description */}
          <p className="text-gray-600 text-sm mb-4 line-clamp-2">
            {property.description}
          </p>

          {/* Property Info Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-4">
            {property.bedrooms && (
              <div className="text-sm">
                <span className="text-gray-500">Bedrooms:</span>{" "}
                <span className="font-medium">{property.bedrooms}</span>
              </div>
            )}
            {property.bathrooms && (
              <div className="text-sm">
                <span className="text-gray-500">Bathrooms:</span>{" "}
                <span className="font-medium">{property.bathrooms}</span>
              </div>
            )}
            {property.size && (
              <div className="text-sm">
                <span className="text-gray-500">Size:</span>{" "}
                <span className="font-medium">{property.size} sqft</span>
              </div>
            )}
          </div>

          {/* View Details Link */}
          <div className="mt-4">
            <Link
              to={`/property/${property.id}`}
              className="inline-flex items-center text-propertylk-blue hover:text-propertylk-darkblue transition-colors group"
            >
              <span className="font-medium border-b-2 border-transparent group-hover:border-current">
                View Details
              </span>
              <ArrowRight className="ml-1 h-4 w-4 transform group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
});

PropertyListItem.displayName = "PropertyListItem";

export default PropertyListItem;
